import { useState, useEffect, useRef, useCallback } from "react";
import { DEFAULT_TASK_DURATION } from "../config";

export function useTaskTimer(task, onUpdateTask, autoCompleteOnTimer) {
  const initialRemaining = Number.isFinite(task.remaining)
    ? task.remaining
    : Number.isFinite(task.duration)
    ? task.duration
    : DEFAULT_TASK_DURATION || 1800;

  const [displayRemaining, setDisplayRemaining] = useState(initialRemaining);
  const intervalRef = useRef(null);
  const lastTickRef = useRef(Date.now());

  useEffect(() => {
    const newInitialRemainingVal = Number.isFinite(task.remaining)
      ? task.remaining
      : Number.isFinite(task.duration)
      ? task.duration
      : DEFAULT_TASK_DURATION || 1800;

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useTaskTimer EFFECT task changed] Task ID: ${task.id}, Name: ${task.name}`
      );
      console.log(
        `  task.remaining: ${task.remaining}, task.duration: ${task.duration}, task.paused: ${task.paused}`
      );
      console.log(
        `  Calculated newInitialRemainingVal: ${newInitialRemainingVal}`
      );
      console.log(`  Current displayRemaining before set: ${displayRemaining}`);
    }

    setDisplayRemaining(newInitialRemainingVal);
    lastTickRef.current = Date.now();

    clearInterval(intervalRef.current); // Clear previous interval before setting a new one

    if (!task.paused && !task.completed && newInitialRemainingVal > 0) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[useTaskTimer EFFECT task changed] Setting up interval for active task: ${task.id}`
        );
      }
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - lastTickRef.current) / 1000);
        lastTickRef.current = now;

        setDisplayRemaining((prev) => {
          const newRemainingTime = Math.max(0, prev - elapsedSeconds);
          if (newRemainingTime <= 0) {
            clearInterval(intervalRef.current);
            if (process.env.NODE_ENV === "development") {
              console.log(
                `[useTaskTimer INTERVAL] Timer ran out for task: ${task.id}`
              );
            }
            if (autoCompleteOnTimer) {
              onUpdateTask(task.id, {
                completed: true,
                paused: true,
                remaining: 0,
              });
            } else {
              onUpdateTask(task.id, { paused: true, remaining: 0 });
            }
            return 0;
          }
          return newRemainingTime;
        });
      }, 1000);
    } else {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[useTaskTimer EFFECT task changed] Not setting interval for task: ${task.id} (paused: ${task.paused}, completed: ${task.completed}, remaining: ${newInitialRemainingVal})`
        );
      }
    }

    return () => {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[useTaskTimer EFFECT cleanup] Clearing interval for task: ${task.id}`
        );
      }
      clearInterval(intervalRef.current);
    };
  }, [
    task.id,
    task.duration,
    task.remaining,
    task.paused,
    task.completed,
    autoCompleteOnTimer,
    onUpdateTask,
  ]);

  const updateTimerIntervalLogic = useCallback(() => {
    clearInterval(intervalRef.current);

    if (task.paused || task.completed || displayRemaining <= 0) {
      if (process.env.NODE_ENV === "development") {
        console.log(
          `[useTaskTimer updateTimerIntervalLogic] Conditions met to clear/not start interval for task ${task.id}: paused=${task.paused}, completed=${task.completed}, displayRemaining=${displayRemaining}`
        );
      }
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useTaskTimer updateTimerIntervalLogic] Setting interval for task ${task.id}. displayRemaining: ${displayRemaining}`
      );
    }
    lastTickRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsedSeconds = Math.floor((now - lastTickRef.current) / 1000);

      setDisplayRemaining((prevDisplayRemaining) => {
        const newRemainingTime = Math.max(
          0,
          prevDisplayRemaining - elapsedSeconds
        );
        lastTickRef.current = now; // Update tick ref here, aligned with state update

        if (newRemainingTime <= 0) {
          clearInterval(intervalRef.current);
          if (process.env.NODE_ENV === "development") {
            console.log(
              `[useTaskTimer INTERVAL (updateTimer)] Timer ran out for task: ${task.id}`
            );
          }
          if (autoCompleteOnTimer) {
            onUpdateTask(task.id, {
              completed: true,
              paused: true,
              remaining: 0,
            });
          } else {
            onUpdateTask(task.id, { paused: true, remaining: 0 });
          }
          return 0;
        }
        return newRemainingTime;
      });
    }, 1000);
  }, [
    task.id,
    task.paused,
    task.completed,
    displayRemaining,
    autoCompleteOnTimer,
    onUpdateTask,
  ]);

  useEffect(() => {
    // This effect now primarily calls the interval logic function.
    // The main `useEffect` above handles initial setup and changes due to task prop updates.
    // This one ensures the interval logic is re-evaluated if displayRemaining changes for an active timer.
    if (!task.paused && !task.completed && displayRemaining > 0) {
      updateTimerIntervalLogic();
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [task.paused, task.completed, displayRemaining, updateTimerIntervalLogic]);

  const start = useCallback(() => {
    if (!task.paused || task.completed || displayRemaining <= 0) return;
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useTaskTimer START] Task: ${task.id}. Current displayRemaining: ${displayRemaining}`
      );
    }
    lastTickRef.current = Date.now(); // Ensure lastTick is fresh before unpausing
    onUpdateTask(task.id, { paused: false });
    // The main useEffect will pick up the change in task.paused and manage the interval.
  }, [task.id, task.paused, task.completed, displayRemaining, onUpdateTask]);

  const pause = useCallback(() => {
    if (task.paused || task.completed) return;
    // Capture the displayRemaining value at the moment pause is called.
    const currentDisplayRemaining = displayRemaining;
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useTaskTimer PAUSE] Task: ${task.id}. Capturing displayRemaining: ${currentDisplayRemaining}. Sending to onUpdateTask.`
      );
    }
    clearInterval(intervalRef.current);
    onUpdateTask(task.id, { paused: true, remaining: currentDisplayRemaining });
    // The main useEffect will pick up the change in task.paused and task.remaining.
  }, [task.id, task.paused, task.completed, displayRemaining, onUpdateTask]);

  const reset = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      console.log(`[useTaskTimer RESET] Task: ${task.id}`);
    }
    clearInterval(intervalRef.current);
    const newRemainingOnReset = task.duration || DEFAULT_TASK_DURATION || 1800;
    // setDisplayRemaining(newRemainingOnReset); // Let the effect triggered by onUpdateTask handle this
    onUpdateTask(task.id, {
      paused: true,
      remaining: newRemainingOnReset,
      completed: false,
    });
    // The main useEffect will pick up the change in task.remaining and task.paused.
  }, [task.id, task.duration, onUpdateTask]);

  return { displayRemaining, start, pause, reset };
}
